import { useCallback } from 'react';
import { useDragControls, type DragControls, type PanInfo } from 'framer-motion';

/**
 * useSwipeToDismiss — Framer Motion drag-based swipe-to-dismiss helper.
 *
 * Uses `dragControls` so the dismiss-gesture is initiated **only from the
 * drag handle**, not from the entire panel surface. This is essential on
 * mobile bottom-sheets: without it, Framer Motion's drag listener claims
 * touch events anywhere on the panel and the inner scrollable content
 * cannot scroll.
 *
 * Part of F5.4 — Mobile Experience (APP_FEEL_2027_UPGRADE).
 *
 * @example Bottom sheet
 * const swipe = useSwipeToDismiss({ onDismiss: onClose, disabled: !isOpen })
 * <motion.div {...swipe.dragProps}>
 *   <div {...swipe.handleProps}>...drag handle...</div>
 *   <div className="overflow-y-auto">...scrollable content...</div>
 * </motion.div>
 *
 * @example Right-side slide-over (full-panel drag is fine — only x-axis)
 * const swipe = useSwipeToDismiss({
 *   onDismiss: onClose,
 *   direction: 'right',
 *   wholePanelDraggable: true,
 * })
 */

type Direction = 'bottom' | 'top' | 'right' | 'left';
interface UseSwipeToDismissOptions {
  /** Called when the user completes a dismiss gesture. */
  onDismiss: () => void;
  /** Pixel offset on the drag axis required to dismiss. Default 120. */
  offsetThreshold?: number;
  /** Velocity (px/s) required to dismiss regardless of offset. Default 600. */
  velocityThreshold?: number;
  /**
   * Direction the panel exits to when dismissed. Default 'bottom'.
   * - 'bottom': bottom sheet, swipe down to close
   * - 'top':    top banner, swipe up to close
   * - 'right':  right-side slide-over, swipe right to close
   * - 'left':   left-side slide-over, swipe left to close
   */
  direction?: Direction;
  /** Disable the gesture (e.g. when sheet is closed). */
  disabled?: boolean;
  /**
   * If true, the entire panel listens for drag (not via controls).
   * Only safe for x-axis (right/left) panels with vertical scrolling
   * content, because dragDirectionLock prevents y-scroll hijacking.
   * For bottom-sheets (y-axis), always keep this false (default) to
   * avoid scroll vs drag conflicts.
   */
  wholePanelDraggable?: boolean;
}
interface UseSwipeToDismissResult {
  /** Spread on the outer `motion.div` for drag behaviour. */
  dragProps: {
    drag: 'y' | 'x' | false;
    dragControls: DragControls;
    dragListener: boolean;
    dragDirectionLock: boolean;
    dragConstraints: {
      top: number;
      bottom: number;
      left: number;
      right: number;
    };
    dragElastic: {
      top: number;
      bottom: number;
      left: number;
      right: number;
    };
    dragMomentum: boolean;
    onDragEnd: (event: PointerEvent | MouseEvent | TouchEvent, info: PanInfo) => void;
  };
  /**
   * Spread on the visual drag handle. Sets `touch-action: none` AND
   * starts the drag controls on pointer-down so only the handle
   * initiates the dismiss gesture.
   */
  handleProps: {
    style: {
      touchAction: 'none';
    };
    onPointerDown: (event: React.PointerEvent) => void;
  };
}
const AXIS_FOR_DIRECTION: Record<Direction, 'x' | 'y'> = {
  bottom: 'y',
  top: 'y',
  right: 'x',
  left: 'x'
};
export function useSwipeToDismiss({
  onDismiss,
  offsetThreshold = 120,
  velocityThreshold = 600,
  direction = 'bottom',
  disabled = false,
  wholePanelDraggable = false
}: UseSwipeToDismissOptions): UseSwipeToDismissResult {
  const axis = AXIS_FOR_DIRECTION[direction];
  const controls = useDragControls();
  const handleDragEnd = useCallback((_event: PointerEvent | MouseEvent | TouchEvent, info: PanInfo) => {
    if (disabled) return;
    const offset = axis === 'y' ? info.offset.y : info.offset.x;
    const velocity = axis === 'y' ? info.velocity.y : info.velocity.x;
    const isPositiveDirection = direction === 'bottom' || direction === 'right';
    const shouldDismiss = isPositiveDirection ? offset > offsetThreshold || velocity > velocityThreshold : offset < -offsetThreshold || velocity < -velocityThreshold;
    if (shouldDismiss) onDismiss();
  }, [onDismiss, offsetThreshold, velocityThreshold, direction, axis, disabled]);
  const handlePointerDown = useCallback((event: React.PointerEvent) => {
    if (disabled) return;
    controls.start(event);
  }, [controls, disabled]);
  const elastic = {
    top: 0,
    bottom: 0,
    left: 0,
    right: 0
  };
  if (direction === 'bottom') elastic.bottom = 0.6;else if (direction === 'top') elastic.top = 0.6;else if (direction === 'right') elastic.right = 0.6;else if (direction === 'left') elastic.left = 0.6;
  return {
    dragProps: {
      drag: disabled ? false : axis,
      dragControls: controls,
      // CRITICAL: when wholePanelDraggable=false, the panel itself does NOT
      // listen for drag — only the handle (via controls.start) starts it.
      // This decouples drag from the panel's scrollable inner content.
      dragListener: wholePanelDraggable && !disabled,
      dragDirectionLock: true,
      dragConstraints: {
        top: 0,
        bottom: 0,
        left: 0,
        right: 0
      },
      dragElastic: elastic,
      dragMomentum: false,
      onDragEnd: handleDragEnd
    },
    handleProps: {
      style: {
        touchAction: 'none'
      },
      onPointerDown: handlePointerDown
    }
  };
}